type LocalizedValue = string | number | { value?: string | number };

export type RawDocumentModule = {
  slug?: string;
  parent_slug?: string;
  entry_slug?: string;
  front_permission?: string;
  route_path?: string;
  name?: LocalizedValue;
  title_key?: string;
  description_key?: string;
  kind?: string;
  sort_order?: number;
};

export type DocumentLibrary = RawDocumentModule & {
  slug?: string;
  parentSlug: string | null;
  entrySlug: string | null;
  frontPermission?: string;
  routePath: string | null;
  title: string | null;
  titleKey: string | null;
  descriptionKey: string;
  groupTitleKey: string | null;
  externalUrl: string | null;
};

type ModulesCollection = RawDocumentModule[] | Record<string, RawDocumentModule>;

const HIDDEN_FRONT_PERMISSIONS = new Set(["webservice.documents.etraining.listing"]);

const EXTERNAL_LIBRARY_URLS: Record<string, string> = {
  reglementaires_myformulation: "https://formule.guinot.com/fr",
};

export function isFrontPermissionVisible(permission?: string): boolean {
  return !permission || !HIDDEN_FRONT_PERMISSIONS.has(permission);
}

function trimSlashes(value = ""): string {
  return String(value).replace(/^\/+|\/+$/g, "");
}

function normalizeTextValue(value?: LocalizedValue): string | null {
  if (typeof value === "string") {
    return value;
  }

  if (value && typeof value === "object") {
    if (typeof value.value === "string") {
      return value.value;
    }

    if (typeof value.value === "number") {
      return String(value.value);
    }
  }

  if (typeof value === "number") {
    return String(value);
  }

  return null;
}

function normalizeRoutePath(routePath = ""): string | null {
  const cleanedPath = trimSlashes(routePath);
  return cleanedPath ? `${BASE_PATH}/${cleanedPath}` : null;
}

function normalizeModulesCollection(modules: ModulesCollection = []): RawDocumentModule[] {
  if (Array.isArray(modules)) {
    return modules;
  }

  if (modules && typeof modules === "object") {
    return Object.values(modules);
  }

  return [];
}

export function normalizeDocumentModule(module?: RawDocumentModule | null): DocumentLibrary | null {
  if (!module) {
    return null;
  }

  return {
    ...module,
    slug: module.slug,
    parentSlug: module.parent_slug || null,
    entrySlug: module.entry_slug || null,
    frontPermission: module.front_permission,
    routePath: normalizeRoutePath(module.route_path),
    title: normalizeTextValue(module.name),
    titleKey: module.title_key || null,
    descriptionKey:
      module.description_key || `document_library.libraries.${module.slug}.description`,
    groupTitleKey: module.parent_slug ? `navigation.modules.${module.parent_slug}` : null,
    externalUrl: module.slug ? EXTERNAL_LIBRARY_URLS[module.slug] || null : null,
  };
}

export function getDocumentLibraries(modules: ModulesCollection = []): DocumentLibrary[] {
  return normalizeModulesCollection(modules)
    .filter(
      (module) =>
        module && module.kind === "library" && isFrontPermissionVisible(module.front_permission)
    )
    .map(normalizeDocumentModule)
    .filter((module): module is DocumentLibrary => {
      return !!(module && module.slug && module.parentSlug && module.entrySlug && module.routePath);
    });
}

export function getDocumentLibrary(
  modules: ModulesCollection = [],
  moduleSlug?: string
): DocumentLibrary | null {
  return getDocumentLibraries(modules).find((module) => module.slug === moduleSlug) || null;
}

export function getDocumentLibraryByRoute(
  modules: ModulesCollection = [],
  parentSlug?: string,
  entrySlug?: string
): DocumentLibrary | null {
  return (
    getDocumentLibraries(modules).find(
      (module) => module.parentSlug === parentSlug && module.entrySlug === entrySlug
    ) || null
  );
}

export function getDocumentLibraryGroupByParent(
  modules: ModulesCollection = [],
  parentSlug?: string
): { parentSlug: string; titleKey: string; libraries: DocumentLibrary[] } | null {
  if (!parentSlug) {
    return null;
  }

  const libraries = getDocumentLibraries(modules)
    .filter((module) => module.parentSlug === parentSlug)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  if (libraries.length === 0) {
    return null;
  }

  return {
    parentSlug,
    titleKey: `navigation.modules.${parentSlug}`,
    libraries,
  };
}

export function getDocumentLibraryGroups(modules: ModulesCollection = []): Array<{
  parentSlug: string;
  titleKey: string;
  libraries: DocumentLibrary[];
  sortOrder: number;
}> {
  const groups = new Map<
    string,
    { parentSlug: string; titleKey: string; libraries: DocumentLibrary[]; sortOrder: number }
  >();

  getDocumentLibraries(modules).forEach((module) => {
    if (!module.parentSlug) {
      return;
    }

    if (!groups.has(module.parentSlug)) {
      groups.set(module.parentSlug, {
        parentSlug: module.parentSlug,
        titleKey: `navigation.modules.${module.parentSlug}`,
        libraries: [],
        sortOrder: module.sort_order || 0,
      });
    }

    const group = groups.get(module.parentSlug);
    if (group) {
      group.libraries.push(module);
      group.sortOrder = Math.min(group.sortOrder, module.sort_order || 0);
    }
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      libraries: group.libraries.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder);
}
